import { Button, message, Tabs } from "antd";
import React, { useState, useEffect, useRef } from "react";
import ChiefComplaintsForm from "../OpFormComponents/ChiefComplaintsForm";
import OPExaminationForm from "../OpFormComponents/OPExaminationForm";
import OpFunctionalDiagnosis from "../CardiopulmonaryForm/OpFunctionalDiagnosis";
import OpExerciseForm from "../OpFormComponents/OpExercise/OpExerciseForm";
import ReviewNotes from "../OpFormComponents/ReviewNotes";
import OpHistoryForm from "../OpFormComponents/OpHistoryForm";
import SportsHistoryForm from "./SportsHistoryForm";
import SportsTherapy from "./SportsTherapy";
import SportsSummary from "./SportsSummary";
import SpecialTestsForm from "../OpFormComponents/SpecialTestsForm";
import { opServiceAPI } from "../../../../../services/opphysio.services";
import { theme } from "antd";
import PhysioScalesOp from "../../../../IpEMR/InspectionForms/Examination/PhysioScales/PhysioScalesOp";
import { opConfirmModal } from "../../opConfirmModal";
import "../../OPForms.css";
const SPORTS_SCALE_TAB_MAP = {
  4: "MMT",
  5: "FingerGripTest",
  6: "ROM",
  7: "TrunkControlTest",
  8: "BarthelIndex",
};
const isMobileView = () => window.innerWidth <= 768;

const SPORTS_SCALE_KEY_NORMALIZER = {
  mmt: "mmt",
  fingergriptest: "finger_grip_test",
  rom: "rom",
  trunkcontroltest: "tcms",
  barthelindex: "barthel",
  specialtests: "special_tests",
};

const tabItems = [
  { label: "Chief Complaints", key: "1" },
  { label: "History", key: "2" },
  { label: "Examination", key: "3" },
  { label: "MMT", key: "4" },
  { label: "Finger Grip Test", key: "5" },
  { label: "ROM", key: "6" },
  { label: "TCMS", key: "7" },
  { label: "Barthel Index", key: "8" },
  { label: "Special Tests", key: "9" },
  { label: "Functional Diagnosis", key: "10" },
  { label: "Therapy", key: "11" },
  { label: "Exercise", key: "12" },
  { label: "Review Notes", key: "13" },
  { label: "Summary", key: "14" },
];
const getTabLabel = (key) => {
  const tab = tabItems.find((item) => item.key === key);
  return tab?.label || "Form";
};
const getFirstFilledTab = (data) => {
  const keys = [
    "chief_complaints",
    "history",
    "examination",
    "mmt",
    "finger_grip",
    "rom",
    "tcms",
    "barthel_index",
    "special_tests",
    "functional_diagnosis",
    "therapy",
    "exercise",
    "review_notes",
    "summary",
  ];
  for (let i = 0; i < keys.length; i++) {
    if (data?.[keys[i]]) return (i + 1).toString();
  }
  return "1";
};

const SportsForm = ({
  admissionId,
  handleClose,
  formData: initialData = {},
  onSave,
  patientData,
}) => {
  const [formData, setFormData] = useState(initialData);
  const lastSavedRef = useRef(initialData);
  const [activeTab, setActiveTab] = useState("1");
  const [isDirty, setIsDirty] = useState(false);
  const [activeScaleKey, setActiveScaleKey] = useState(null);
  const scaleSaveRef = useRef(null);
  const { token } = theme.useToken();
  const onValuesChange = (key, changedValues, meta) => {
    if (key === "exercise" && Array.isArray(changedValues)) {
      setFormData((prev) => ({
        ...prev,
        exercise: changedValues,
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          exercise: changedValues,
        };
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          ...changedValues,
        },
      }));
      if (meta?.hydrateOnly) {
        lastSavedRef.current = {
          ...(lastSavedRef.current || {}),
          [key]: {
            ...((lastSavedRef.current || {})[key] || {}),
            ...changedValues,
          },
        };
      }
    }
    if (!meta?.hydrateOnly) {
      setIsDirty(true);
    }
  };
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const handleTabChange = (nextKey) => {
    const currentScaleKey = SPORTS_SCALE_TAB_MAP[activeTab] || null;
    const nextScaleKey = SPORTS_SCALE_TAB_MAP[nextKey] || null;

    if (isDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content: "Save and proceed?",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          let payload = { ...formData };

          // IMPORTANT: save the currently-open scale (if any), not the next tab's scale
          if (currentScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();

            if (scaleData) {
              const normalizedKey =
                SPORTS_SCALE_KEY_NORMALIZER[
                  currentScaleKey.replaceAll("_", "").toLowerCase()
                ] || currentScaleKey.toLowerCase();

              payload[normalizedKey] = {
                ...(formData[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          await submitWithPayload({
            ...payload,
            admission_id: admissionId || formData?.admission_id,
          });

          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
          setIsDirty(false);
        },
        onCancel: () => {
          // Discard changes and proceed to the clicked tab
          setFormData(lastSavedRef.current || initialData);
          setIsDirty(false);
          setActiveTab(nextKey);
          setActiveScaleKey(nextScaleKey);
        },
      });
    } else {
      setActiveTab(nextKey);
      setActiveScaleKey(nextScaleKey);
    }
  };

  const renderTab = () => {
    if (activeScaleKey) {
      return (
        <PhysioScalesOp
          ref={scaleSaveRef}
          admission={admissionId}
          activeScaleKey={activeScaleKey}
          editData={formData}
          notifyOnSave={true}
          op={true}
          isNeuro={true}
          onClose={handleCloseWithCheck}
          onScaleDirty={() => setIsDirty(true)}
          onScalesSubmit={(key, data) => {
            if (!data || typeof data !== "object") return;

            const normalizedKey =
              SPORTS_SCALE_KEY_NORMALIZER[
                key.replaceAll("_", "").toLowerCase()
              ] || key.toLowerCase();

            setFormData((prev) => ({
              ...prev,
              [normalizedKey]: data,
            }));
          }}
          patientData={patientData}
        />
      );
    }
    switch (activeTab) {
      case "1":
        return (
          <ChiefComplaintsForm
            onValuesChange={onValuesChange}
            formData={formData}
            formType="sports"
          />
        );
      case "2":
        return (
          <OpHistoryForm onValuesChange={onValuesChange} formData={formData}>
            <SportsHistoryForm />
          </OpHistoryForm>
        );
      case "3":
        return (
          <OPExaminationForm
            onValuesChange={onValuesChange}
            formData={formData}
          />
        );
      case "9":
        return (
          <SpecialTestsForm
            onValuesChange={onValuesChange}
            formData={formData}
          />
        );
      case "10":
        return (
          <OpFunctionalDiagnosis
            onValuesChange={onValuesChange}
            formData={formData}
            formType="sports"
          />
        );
      case "11":
        return (
          <SportsTherapy onValuesChange={onValuesChange} formData={formData} />
        );
      case "12":
        return (
          <OpExerciseForm onValuesChange={onValuesChange} formData={formData} />
        );
      case "13":
        return (
          <ReviewNotes onValuesChange={onValuesChange} formData={formData} />
        );
      case "14":
        return <SportsSummary formData={formData} />;
      default:
        return null;
    }
  };
  const handleCloseWithCheck = () => {
    const actuallyDirty =
      isDirty ||
      JSON.stringify(formData || {}) !==
        JSON.stringify(lastSavedRef.current || initialData || {});

    if (actuallyDirty) {
      opConfirmModal({
        title: "Unsaved changes",
        content:
          "You have unsaved changes. Do you want to save these changes before closing?",
        okText: "Save & Close",
        cancelText: "Close without saving",
        okButtonProps: {
          style: {
            backgroundColor: token.colorPrimary,
            borderColor: token.colorPrimary,
            color: "#fff",
          },
        },
        onOk: async () => {
          const actualAdmissionId = admissionId || formData?.admission_id;
          if (!actualAdmissionId) {
            message.error("Admission ID is required.");
            return;
          }

          let payload = { ...formData, admission_id: actualAdmissionId };

          if (activeScaleKey && scaleSaveRef.current) {
            const scaleData = await scaleSaveRef.current.saveScale();
            if (scaleData) {
              const normalizedKey =
                SPORTS_SCALE_KEY_NORMALIZER[
                  activeScaleKey.replaceAll("_", "").toLowerCase()
                ] || activeScaleKey.toLowerCase();

              payload[normalizedKey] = {
                ...(payload[normalizedKey] || {}),
                ...scaleData,
              };
            }
          }

          await submitWithPayload(payload);
          setIsDirty(false);
          handleClose();
        },
        onCancel: () => {
          // Discard changes and close
          setFormData(lastSavedRef.current || initialData);
          setIsDirty(false);
          handleClose();
        },
      });
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    try {
      const actualAdmissionId = admissionId || formData?.admission_id;
      if (!actualAdmissionId) {
        message.error("Admission ID is required.");
        return;
      }

      const payload = { ...formData, admission_id: actualAdmissionId };
      delete payload.TrunkControlTest;
      delete payload.barthel_index;

      const method = payload?.id ? "PATCH" : "POST";

      const response = await opServiceAPI("sports", payload, method);
      const returned = response?.data?.data;

      message.success(
        method === "POST"
          ? "Sports EMR saved successfully."
          : "Sports EMR updated successfully."
      );
      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
        id: returned?.id ?? prev?.id,
      }));

      if (onSave && activeTab === "14") {
        onSave(payload);
      }

      setIsDirty(false);
    } catch (error) {
      console.error("API error:", error.response || error);
      const errorMessage =
        error?.response?.data?.message || "Failed to save form.";
      message.error(errorMessage);
    }
  };
  const submitWithPayload = async (payload) => {
    try {
      if (payload && typeof payload === "object") {
        delete payload.TrunkControlTest;
        delete payload.barthel_index;
      }
      const method = payload?.id ? "PATCH" : "POST";

      const response = await opServiceAPI("sports", payload, method);
      const returned = response?.data?.data;

      setFormData((prev) => ({
        ...prev,
        ...payload,
        ...returned,
        id: returned?.id ?? prev?.id,
      }));
      lastSavedRef.current = {
        ...(lastSavedRef.current || {}),
        ...payload,
        ...returned,
        id: returned?.id ?? formData?.id,
      };

      const tabLabel = getTabLabel(activeTab);
      message.success(`${tabLabel} saved successfully`);

      setIsDirty(false);
    } catch (error) {
      message.error("Failed to save Sports EMR.");
    }
  };

  const handleButton = async () => {
    const actualAdmissionId = admissionId || formData?.admission_id;

    if (activeTab === "14") {
      await handleSubmit();
      handleClose();
      return;
    }

    let payload = {
      ...formData,
      admission_id: actualAdmissionId,
    };

    if (activeScaleKey && scaleSaveRef.current) {
      const scaleData = await scaleSaveRef.current.saveScale();

      if (scaleData) {
        const normalizedKey =
          SPORTS_SCALE_KEY_NORMALIZER[
            activeScaleKey.replaceAll("_", "").toLowerCase()
          ] || activeScaleKey.toLowerCase();

        payload[normalizedKey] = {
          ...(formData[normalizedKey] || {}),
          ...scaleData,
        };
      }
    }

    await submitWithPayload(payload);

    const nextTab = (parseInt(activeTab) + 1).toString();
    setActiveTab(nextTab);
    setActiveScaleKey(SPORTS_SCALE_TAB_MAP[nextTab] || null);
  };

  return (
    <>
      <div className="op-assessment-tabs" style={{ background: "white" }}>
        {/* <h2 style={{ margin: 0, fontWeight: 600, textAlign: "center" }}>
          Sports EMR
        </h2> */}
        <Tabs
          items={tabItems}
          activeKey={activeTab}
          onChange={handleTabChange}
          moreIcon={null}
        />
      </div>
      <div style={{ flex: 1, overflow: "auto", position: "relative" }}>
        <div
          className="op-assessment-panel"
          style={{
            height: "100vh",
            overflowY: "auto",
            backgroundColor: "#f6f5ff",
            borderRadius: "5px",
            border: "1px solid #e6e6e6",
            paddingBottom: "70px",
          }}
        >
          <div className="ia op-assessment-form-body">{renderTab()}</div>
        </div>
      </div>
      <div
        style={{
          position: "fixed",
          bottom: 0,
          right: 0,
          width: "100%",
          background: "#fff",
          padding: "10px 20px",
          borderTop: "1px solid #e6e6e6",
          display: "flex",
          justifyContent: "flex-end",
          gap: "12px",
          zIndex: 1000,
        }}
      >
        <Button onClick={handleCloseWithCheck}>Cancel</Button>
        <Button type="primary" onClick={handleButton}>
          {activeTab === "14" ? "Save" : "Next"}
        </Button>
      </div>
    </>
  );
};

export default SportsForm;
